type MessageLike = { id: string; role: string; parts: ReadonlyArray<unknown> };

// One rendered turn: a user message, or a run of consecutive assistant
// messages shown as a single reply. Live, useChat keeps a whole reply in one
// assistant message; a chat reloaded from GET /chat/:id comes back as one
// assistant message per model step, which would otherwise render as a stack
// of one-step replies.
export type ChatTurn<M extends MessageLike> = {
  // Id of the first message: stays stable while later steps join the run.
  key: string;
  messages: ReadonlyArray<M>;
  // The last message carries the id and metadata the reply's actions use.
  last: M;
  parts: ReadonlyArray<M['parts'][number]>;
};

const sameMessages = <M>(a: ReadonlyArray<M>, b: ReadonlyArray<M>): boolean =>
  a.length === b.length && a.every((message, i) => message === b[i]);

// Set live on the finish chunk and, on reload, on the last message of a cut
// reply.
export const isCutOffReply = (metadata: unknown): boolean =>
  typeof metadata === 'object' &&
  metadata != null &&
  'finishReason' in metadata &&
  metadata.finishReason === 'length';

// `previous` is the last result: a turn whose messages are unchanged is reused
// as is, so memoized message rows skip re-rendering while a reply streams.
export const groupChatTurns = <M extends MessageLike>(
  messages: ReadonlyArray<M>,
  previous: ReadonlyArray<ChatTurn<M>> = [],
): Array<ChatTurn<M>> => {
  const runs: Array<Array<M>> = [];
  for (const message of messages) {
    const run = runs[runs.length - 1];
    if (
      run != null &&
      message.role === 'assistant' &&
      run[0]?.role === 'assistant'
    ) {
      run.push(message);
    } else {
      runs.push([message]);
    }
  }
  const previousByKey = new Map(previous.map(turn => [turn.key, turn]));
  return runs.map(run => {
    const first = run[0] as M;
    const last = run[run.length - 1] as M;
    const cached = previousByKey.get(first.id);
    if (cached != null && sameMessages(cached.messages, run)) {
      return cached;
    }
    return {
      key: first.id,
      messages: run,
      last,
      parts:
        run.length === 1 ? last.parts : run.flatMap(message => message.parts),
    };
  });
};
